import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Tab variants — mirror the Figma "Flashka Design System" `Tab` (file
 * `YZK9r3wDhvw1pY60KlVEIj`). Reference nodes:
 *   - 2363:3608 — the Tab item component set (all 6 states × 3 types), light.
 *   - 2812:44313 — the Tab item component set, DARK.
 *   - 2363:4265 — assembled Tab rows (type × 3–10 items × separator), light.
 *   - 2812:44404 — assembled Tab rows, DARK.
 *
 * DARK MODE — every colour in the dark nodes is the exact auto-inverted value
 * of its light token (verified via get_variable_defs 2026-07-17: secondary-800
 * #262626→#e5e5e5 · secondary-600 #525252→#a1a1a1 · secondary-50 #fafafa→
 * #0a0a0a · secondary-100 #f5f5f5→#171717 · separator secondary-200 #e5e5e5→
 * #262626), so NO `dark:` classes are needed — the palette inverts itself.
 *
 * ANIMATION — the active 3px underline (Figma "Select" bar) lives on a
 * SEPARATE moving layer, one measured overlay per tab list, slid between items
 * (CSS transform+width transition on web; reanimated on native) — the same
 * model as SegmentedControl's active pill, whose animation module this
 * component reuses.
 *
 * ANATOMY — each item is a column: the pressable content row (8px padding,
 * 8px icon–label gap, 12px radius — Figma "Text container") over a 3px
 * underline slot. EVERY item reserves the slot (uniform 39px height) so the
 * measured underline overlay can slide along the row bottom; Figma instead
 * vertically centres the 36px inactive items against the 39px active one,
 * which offsets their labels 1.5px — reserving the slot keeps all labels on
 * one baseline, which is the evident intent.
 */

/**
 * A single tab item's content row (the pressable). Owns geometry, the
 * hover/pressed washes, focus, and the text/icon colour.
 *
 * Figma state map (2363:3608):
 *   - Default — transparent, `secondary-600` text/icon.
 *   - Hover   — `secondary-100/50` wash on the content row (12px radius).
 *     The node paints an OPAQUE `secondary-50`, but the deck headers the Tab
 *     sits on are themselves `secondary-50` (hover invisible) and the dotted
 *     canvas is darker (a lighter opaque chip reads as fake elevation) — so
 *     we deviate to a translucent darkening wash that shows on any surface.
 *   - Pressed — `secondary-100` wash (+ the node's ~0.97 scale, rendered as
 *     `web:active:scale-[0.98]` — the DS press scale).
 *   - Focus   — flush 1px `secondary-600` border; rendered as a
 *     `ring-offset-0` ring (same proven pattern as SegmentedControl/Button).
 *     The node draws it at a 14px outer radius vs the content row's 12px —
 *     same box, 2px radius delta, visually identical on a 1px line.
 *   - Active  — `secondary-800` text/icon (+ the underline overlay).
 *   - Disabled — `secondary-800` text/icon at 50% opacity (the node dims the
 *     ACTIVE tone, not the default one — deliberate spec, kept verbatim).
 */
export const tabItemVariants = cva(
  'inline-flex flex-row items-center gap-2 rounded-[12px] border-0 bg-transparent p-2 ' +
    'web:transition-[transform,background-color,color] web:duration-150 web:ease-out ' +
    'web:focus-visible:ring-1 web:focus-visible:ring-offset-0 web:focus-visible:ring-secondary-600 ' +
    'enabled:hover:bg-secondary-100/50 enabled:active:bg-secondary-100 ' +
    'native:active:bg-secondary-100 web:enabled:active:scale-[0.98]',
  {
    variants: {
      active: {
        true: 'text-secondary-800',
        false: 'text-secondary-600',
      },
      disabled: { true: '', false: '' },
    },
    compoundVariants: [
      // Disabled = the ACTIVE tone at half opacity (Figma 2363:3599/3605) —
      // `!opacity-50` also overrides the Pressable primitive's fallback.
      { disabled: true, class: 'text-secondary-800 !opacity-50' },
    ],
    defaultVariants: { active: false, disabled: false },
  },
);

/**
 * Item label — Figma "Caption M Bold" (Mona Sans Bold 14 / line-height 100%).
 * Colour is repeated on the `<Text>` itself because RN Text doesn't inherit
 * `text-*` from the parent Pressable (web cascades identically either way).
 * Keep in sync with `tabItemVariants`' colours and `Tab.icons.ts`.
 */
export const tabLabelVariants = cva(
  'whitespace-nowrap text-sm font-bold !leading-[normal] native:font-mona-bold',
  {
    variants: {
      active: { true: 'text-secondary-800', false: 'text-secondary-600' },
      disabled: { true: '', false: '' },
    },
    compoundVariants: [{ disabled: true, class: 'text-secondary-800' }],
    defaultVariants: { active: false, disabled: false },
  },
);

/**
 * The tab list row (Figma assembled `Tab`, 2363:4265): items sit 4px apart;
 * `relative` anchors the moving underline overlay — on web it's also the
 * items' offsetParent, so measurements are row-relative.
 */
export const tabListClass = 'relative inline-flex flex-row items-center gap-1';

/**
 * `fullWidth`: the row fills its parent and every item takes an equal share
 * (Figma New-IA deck tab card `108:27127`, where both items are
 * `flex-[1_0_0]`). Mirrors SegmentedControl's prop of the same name so the
 * two DS switchers stretch the same way.
 */
export const tabListFullWidthClass = 'flex w-full';
export const tabItemColumnFullWidthClass = 'flex-1';
/** Stretched items fill their half and CENTRE their icon+label (Figma text container). */
export const tabItemFullWidthClass = 'w-full justify-center';

/** Per-item column: content row over the 3px underline slot. */
export const tabItemColumnClass = 'flex flex-col items-stretch';

/** The reserved 3px strip the underline overlay slides along. */
export const tabUnderlineSlotClass = 'h-[3px]';

/**
 * The moving active underline — Figma "Select" bar: 3px tall, full item
 * width, `secondary-800`, fully rounded. Positioned by the platform files.
 */
export const tabUnderlineClass =
  'pointer-events-none absolute h-[3px] rounded-[999px] bg-secondary-800';

/**
 * Vertical separator between items (Figma `Separator=Yes`, 2403:9618): a
 * 1×18px `secondary-200` line, centred by the row, with the row's 4px gap on
 * each side.
 */
export const tabSeparatorClass =
  'h-[18px] w-px shrink-0 rounded-[999px] bg-secondary-200';

export type TabItemVariantProps = VariantProps<typeof tabItemVariants>;
